import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext.tsx';
import { 
  Bars3Icon, 
  XMarkIcon,
  MagnifyingGlassIcon,
  UserIcon
} from '@heroicons/react/24/outline';

interface NavbarProps {
  onSearch?: (searchTerm: string) => void;
  showSearch?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ onSearch, showSearch = false }) => {
  const { user, logout } = useAuth();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const location = useLocation();
  const navigate = useNavigate();

  // Close sidebar when route changes
  useEffect(() => {
    setIsSidebarOpen(false);
    setIsSearchOpen(false);
    setIsDropdownOpen(false);
  }, [location.pathname]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Element;
      if (!target.closest('.navbar-dropdown')) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (onSearch) {
      onSearch(searchTerm);
    } else if (searchTerm.trim()) {
      // Navigate to events page with search term
      navigate(`/events?search=${encodeURIComponent(searchTerm.trim())}`);
    }
  };

  const handleLogout = () => {
    logout();
    setIsSidebarOpen(false);
    setIsDropdownOpen(false);
    navigate('/login');
  };

  return (
    <>
      {/* Mobile Search Overlay */}
      {isSearchOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden">
          <div className="fixed top-0 left-0 right-0 bg-white p-4 z-50">
            <div className="flex items-center gap-3">
              <button
                onClick={() => setIsSearchOpen(false)}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <XMarkIcon className="h-6 w-6" />
              </button>
              <form onSubmit={handleSearch} className="flex-1">
                <div className="relative">
                  <input
                    type="text"
                    placeholder="Search events, artists, venues..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    autoFocus
                  />
                  <MagnifyingGlassIcon className="absolute left-3 top-3 h-5 w-5 text-gray-400" />
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Main Navbar */}
      <nav className="bg-white shadow-lg border-b border-gray-200 fixed top-0 left-0 right-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-[#D6A77A] to-[#FF6B35] rounded-lg flex items-center justify-center mr-2">
                  <span className="text-white font-bold text-sm">L</span>
                </div>
                <span className="text-xl font-bold text-[#222]">LayLow-India</span>
              </div>
            </Link>

            {/* Desktop Navigation - Center */}
            <div className="hidden lg:flex items-center space-x-8">
              <Link 
                to="/" 
                className={`font-medium transition-colors duration-200 ${
                  location.pathname === '/' 
                    ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' 
                    : 'text-gray-700 hover:text-[#FF6B35] hover:border-b-2 hover:border-[#FF6B35]'
                }`}
              >
                Home
              </Link>
              <Link 
                to="/events" 
                className={`font-medium transition-colors duration-200 ${
                  location.pathname === '/events' 
                    ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' 
                    : 'text-gray-700 hover:text-[#FF6B35] hover:border-b-2 hover:border-[#FF6B35]'
                }`}
              >
                Events
              </Link>
              <Link 
                to="/sell-ticket" 
                className={`font-medium transition-colors duration-200 ${
                  location.pathname === '/sell-ticket' 
                    ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' 
                    : 'text-gray-700 hover:text-[#FF6B35] hover:border-b-2 hover:border-[#FF6B35]'
                }`}
              >
                List a Ticket
              </Link>
              {user && (
                <Link 
                  to="/my-tickets" 
                  className={`font-medium transition-colors duration-200 ${
                    location.pathname === '/my-tickets' 
                      ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' 
                      : 'text-gray-700 hover:text-[#FF6B35] hover:border-b-2 hover:border-[#FF6B35]'
                  }`}
                >
                  My Tickets
                </Link>
              )}
              {user?.role === 'PROMOTER' && (
                <Link 
                  to="/promoter/dashboard" 
                  className={`font-medium transition-colors duration-200 ${
                    location.pathname === '/promoter/dashboard' 
                      ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' 
                      : 'text-gray-700 hover:text-[#FF6B35] hover:border-b-2 hover:border-[#FF6B35]'
                  }`}
                >
                  Promoter Dashboard
                </Link>
              )}
              {user?.role === 'ORGANIZER' && (
                <Link 
                  to="/organizer/dashboard" 
                  className={`font-medium transition-colors duration-200 ${
                    location.pathname === '/organizer/dashboard' 
                      ? 'text-[#FF6B35] border-b-2 border-[#FF6B35]' 
                      : 'text-gray-700 hover:text-[#FF6B35] hover:border-b-2 hover:border-[#FF6B35]'
                  }`}
                >
                  Organizer Dashboard
                </Link>
              )}
            </div>

            {/* Desktop Navigation - Right Side */}
            <div className="hidden lg:flex items-center space-x-4">
              {user ? (
                /* Profile Dropdown for logged in users */
                <div className="relative navbar-dropdown">
                  <button
                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                    className={`flex items-center gap-2 px-4 py-2 font-medium rounded-lg transition-all duration-200 hover:bg-gray-100 hover:shadow-md ${
                      location.pathname === '/profile' || location.pathname === '/my-bids' || (user?.role === 'PROMOTER' && location.pathname === '/promoter/dashboard')
                        ? 'text-[#FF6B35] bg-orange-50'
                        : 'text-gray-700 hover:text-[#FF6B35]'
                    }`}
                  >
                    <span>{user.name}</span>
                    <svg 
                      className={`w-4 h-4 transition-transform duration-200 ${isDropdownOpen ? 'rotate-180' : 'rotate-0'}`} 
                      fill="none" 
                      stroke="currentColor" 
                      viewBox="0 0 24 24"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                    </svg>
                  </button>
                  
                  {/* Dropdown with smooth animation */}
                  <div 
                    className={`absolute top-full right-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-50 transition-all duration-200 transform origin-top ${
                      isDropdownOpen 
                        ? 'opacity-100 scale-100 translate-y-0' 
                        : 'opacity-0 scale-95 -translate-y-2 pointer-events-none'
                    }`}
                  >
                    <div className="py-1">
                      <Link
                        to="/profile"
                        className={`block px-4 py-2 transition-all duration-150 hover:bg-gray-100 hover:text-[#FF6B35] hover:pl-6 ${
                          location.pathname === '/profile' ? 'text-[#FF6B35] bg-orange-50' : 'text-gray-700'
                        }`}
                        onClick={() => setIsDropdownOpen(false)}
                      >
                        Profile
                      </Link>
                      <hr className="my-1 border-gray-200" />
                      <button
                        onClick={() => {
                          handleLogout();
                          setIsDropdownOpen(false);
                        }}
                        className="block w-full text-left px-4 py-2 text-red-600 hover:bg-red-50 hover:pl-6 transition-all duration-150"
                      >
                        Logout
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* Single Login/Sign Up button for non-logged in users */
                <Link
                  to="/login"
                  className="flex items-center gap-2 px-4 py-2 font-medium rounded-lg transition-all duration-200 hover:bg-gray-100 hover:shadow-md text-gray-700 hover:text-[#FF6B35]"
                >
                  <span>Login / Sign Up</span>
                </Link>
              )}
            </div>

            {/* Mobile Navigation */}
            <div className="flex items-center space-x-2 lg:hidden">
              {/* Search Button */}
              <button
                onClick={() => setIsSearchOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Search"
              >
                <MagnifyingGlassIcon className="h-6 w-6 text-gray-600" />
              </button>
              
              {/* Hamburger Menu */}
              <button
                onClick={() => setIsSidebarOpen(true)}
                className="p-2 hover:bg-gray-100 rounded-lg"
                aria-label="Open menu"
              >
                <Bars3Icon className="h-6 w-6 text-gray-600" />
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Sidebar */}
      {isSidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden">
          <div className="fixed inset-y-0 right-0 w-64 bg-white shadow-xl z-50">
            <div className="flex flex-col h-full">
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b border-gray-200">
                <h2 className="text-lg font-semibold text-gray-900">Menu</h2>
                <button
                  onClick={() => setIsSidebarOpen(false)}
                  className="p-2 hover:bg-gray-100 rounded-lg"
                >
                  <XMarkIcon className="h-6 w-6" />
                </button>
              </div>

              {/* Navigation Links */}
              <div className="flex-1 px-4 py-6 space-y-4">
                <Link
                  to="/"
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    location.pathname === '/' 
                      ? 'text-[#FF6B35] bg-orange-50' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                  }`}
                >
                  Home
                </Link>
                <Link
                  to="/events"
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    location.pathname === '/events' 
                      ? 'text-[#FF6B35] bg-orange-50' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                  }`}
                >
                  Events
                </Link>
                <Link
                  to="/sell-ticket"
                  className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                    location.pathname === '/sell-ticket' 
                      ? 'text-[#FF6B35] bg-orange-50' 
                      : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                  }`}
                >
                  List a Ticket
                </Link>
                {user ? (
                  <>
                    <Link
                      to="/my-tickets"
                      className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        location.pathname === '/my-tickets' 
                          ? 'text-[#FF6B35] bg-orange-50' 
                          : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                      }`}
                    >
                      My Tickets
                    </Link>
                    {user.role === 'PROMOTER' && (
                      <Link
                        to="/promoter/dashboard"
                        className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                          location.pathname === '/promoter/dashboard' 
                            ? 'text-[#FF6B35] bg-orange-50' 
                            : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                        }`}
                      >
                        Promoter Dashboard
                      </Link>
                    )}
                    {user.role === 'ORGANIZER' && (
                      <Link
                        to="/organizer/dashboard"
                        className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                          location.pathname === '/organizer/dashboard' 
                            ? 'text-[#FF6B35] bg-orange-50' 
                            : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                        }`}
                      >
                        Organizer Dashboard
                      </Link>
                    )}
                    <Link
                      to="/profile"
                      className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        location.pathname === '/profile' 
                          ? 'text-[#FF6B35] bg-orange-50' 
                          : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                      }`}
                    >
                      Profile
                    </Link>
                  </>
                ) : (
                  <>
                    <Link
                      to="/login"
                      className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        location.pathname === '/login' 
                          ? 'text-[#FF6B35] bg-orange-50' 
                          : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                      }`}
                    >
                      Login
                    </Link>
                    <Link
                      to="/register"
                      className={`flex items-center px-3 py-2 rounded-lg font-medium transition-colors duration-200 ${
                        location.pathname === '/register' 
                          ? 'text-[#FF6B35] bg-orange-50' 
                          : 'text-gray-700 hover:bg-gray-100 hover:text-[#FF6B35]'
                      }`}
                    >
                      Sign Up
                    </Link>
                  </>
                )}
              </div>

              {/* Logout Section */}
              {user && (
                <div className="p-4 pb-20 border-t border-gray-200">
                  <div className="flex items-center mb-4">
                    <div className="w-8 h-8 bg-gradient-to-r from-[#D6A77A] to-[#FF6B35] rounded-full flex items-center justify-center mr-3">
                      <UserIcon className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">
                        {user.name}
                      </p>
                      <p className="text-xs text-gray-500">{user.email}</p>
                    </div>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="w-full bg-red-600 text-white py-2 px-4 rounded-lg hover:bg-red-700 font-medium"
                  >
                    Logout
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Navbar; 